import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  CreditCard, 
  Check, 
  Sparkles, 
  Crown,
  Zap,
  TrendingUp,
  DollarSign,
  FileText
} from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscribePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!user) {
      toast.error("Please sign in first");
      navigate("/auth");
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/payments/subscribe`,
        {
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || "Subscription failed";
      toast.error(message);
      setLoading(false);
    }
  };

  // If already Pro, show success message
  if (user?.user_tier === 'pro' || user?.is_premium) {
    return (
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16"
        >
          <div className="w-20 h-20 bg-gradient-to-r from-[#FF0099] to-[#FF6B6B] mx-auto mb-6 flex items-center justify-center rounded-full">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-extrabold tracking-tighter mb-4">
            You're Already a Pro Member!
          </h1>
          <p className="text-gray-500 max-w-md mx-auto mb-8">
            Enjoy personalized feeds, marketplace links, and advanced analytics.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-black text-white hover:bg-[#FF0099] border-2 border-black font-bold"
          >
            Back to Trends
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <Crown className="w-6 h-6 text-[#FF0099]" />
          <span className="text-sm font-mono uppercase tracking-widest text-gray-500">
            PRO SUBSCRIPTION
          </span>
        </div>
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter mb-4">
          Upgrade to <span className="text-[#FF0099]">Trendi Pro</span>
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto text-lg">
          Get personalized trend insights, marketplace access, and automated reports.
          Cancel anytime.
        </p>
      </motion.div>

      {/* Pro Plan Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="neo-card p-8 border-4 border-[#FF0099] relative"
      >
        {/* Popular Badge */}
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF0099] text-white px-6 py-1.5 text-sm font-bold uppercase">
          Most Popular
        </div>
        
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-[#FF0099] to-[#FF6B6B] mx-auto mb-4 flex items-center justify-center rounded-full">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold mb-2">Pro Analyst</h2>
          <div className="flex items-baseline justify-center gap-1 mb-2">
            <span className="text-5xl font-extrabold">$29.99</span>
            <span className="text-gray-500 text-lg">/month</span>
          </div>
          <p className="text-sm text-gray-500">Billed monthly • Cancel anytime</p>
        </div>

        {/* Features */}
        <ul className="space-y-4 mb-8">
          <li className="flex items-start gap-3">
            <Check className="w-5 h-5 mt-0.5 text-[#FF0099] flex-shrink-0" />
            <div>
              <span className="font-bold">Personalized Feed</span>
              <p className="text-sm text-gray-600">AI-powered trend recommendations based on your interests</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Check className="w-5 h-5 mt-0.5 text-[#FF0099] flex-shrink-0" />
            <div>
              <span className="font-bold">Niche Filtering</span>
              <p className="text-sm text-gray-600">Focus on your top categories with discovery mode</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Check className="w-5 h-5 mt-0.5 text-[#FF0099] flex-shrink-0" />
            <div>
              <span className="font-bold">Marketplace Access</span>
              <p className="text-sm text-gray-600">Direct links to Amazon, Target, Etsy for trend products</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Check className="w-5 h-5 mt-0.5 text-[#FF0099] flex-shrink-0" />
            <div>
              <span className="font-bold">Export Reports</span>
              <p className="text-sm text-gray-600">Download top 50 personalized trends as CSV/Excel</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Check className="w-5 h-5 mt-0.5 text-[#FF0099] flex-shrink-0" />
            <div>
              <span className="font-bold">Predictive Analytics</span>
              <p className="text-sm text-gray-600">AI-powered trend predictions and growth forecasts</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Check className="w-5 h-5 mt-0.5 text-[#FF0099] flex-shrink-0" />
            <div>
              <span className="font-bold">Priority Support</span>
              <p className="text-sm text-gray-600">Get help when you need it</p>
            </div>
          </li>
        </ul>

        {/* CTA Button */}
        <Button
          onClick={handleSubscribe}
          disabled={loading || !user}
          className="w-full font-bold py-4 text-lg bg-gradient-to-r from-[#FF0099] to-[#FF6B6B] text-white hover:shadow-xl hover:scale-105 transition-all border-2 border-black"
        >
          {loading ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent animate-spin rounded-full" />
          ) : !user ? (
            "Sign In to Subscribe"
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              Subscribe to Pro - $29.99/month
            </>
          )}
        </Button>

        {/* Trust Indicators */}
        <div className="mt-6 flex items-center justify-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>Instant activation</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>Secure checkout</span>
          </div>
        </div>
      </motion.div>

      {/* Money-back Guarantee */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-8 p-6 bg-green-50 border-2 border-green-200 text-center"
      >
        <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-600" />
        <h3 className="font-bold text-lg mb-1">30-Day Money-Back Guarantee</h3>
        <p className="text-sm text-gray-600">
          Not satisfied? Get a full refund within 30 days, no questions asked.
        </p>
      </motion.div>

      {/* Secure Payment Badge */}
      <div className="mt-8 text-center">
        <div className="inline-flex items-center gap-3 text-gray-400 text-sm">
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
          </svg>
          <span>Powered by Stripe • Secure payments</span>
        </div>
      </div>

      {/* FTC Disclosure */}
      <div className="mt-8 p-4 bg-gray-50 border border-gray-200 text-center">
        <p className="text-xs text-gray-500">
          <span className="font-semibold">FTC Disclosure:</span> As a Trendi partner, we may earn from qualifying purchases made through marketplace links.
        </p>
      </div>
    </div>
  );
};

export default SubscribePage;
